import React, { useState } from "react";
import {
  FaFileCode,
  FaFilePdf,
  FaFileAlt,
  FaMedium,
  FaWordpress,
  FaDev,
  FaHashtag,
  FaLinkedin,
  FaGithub,
} from "react-icons/fa";
import MonoButton from "../kits/DesignKit/MonoButton";
import ModalWrapper from "./utils/ModalWrapper";

function ZenEditorExportModal({ markdown, onClose }) {
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [toast, setToast] = useState(null);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Convert Markdown to HTML
  const markdownToHTML = (md) => {
    let html = md;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

    // Italic
    html = html.replace(/\_(.*?)\_/gim, '<em>$1</em>');
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');

    // Strikethrough
    html = html.replace(/\~\~(.*?)\~\~/gim, '<del>$1</del>');

    // Code inline
    html = html.replace(/`([^`]+)`/gim, '<code>$1</code>');

    // Code blocks
    html = html.replace(/```([a-z]*)\n([\s\S]*?)```/gim, '<pre><code class="language-$1">$2</code></pre>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^\)]+)\)/gim, '<a href="$2">$1</a>');

    // Images
    html = html.replace(/\!\[([^\]]*)\]\(([^\)]+)\)/gim, '<img src="$2" alt="$1" />');

    // Lists
    html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
    html = html.replace(/^\- (.+)$/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/gims, '<ul>$1</ul>');

    // Blockquotes
    html = html.replace(/^\> (.+)$/gim, '<blockquote>$1</blockquote>');

    // HR
    html = html.replace(/^\-\-\-$/gim, '<hr />');

    // Paragraphs
    html = html.replace(/\n\n/g, '</p><p>');
    html = '<p>' + html + '</p>';

    return html;
  };

  // Export as HTML
  const exportAsHTML = () => {
    setExporting(true);
    setExportType('html');

    const htmlContent = markdownToHTML(markdown);
    const fullHTML = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ZenEditor Export</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
      line-height: 1.6;
      color: #333;
    }
    h1, h2, h3 { color: #ff6b35; margin-top: 1.5em; }
    h1 { font-size: 2em; border-bottom: 2px solid #ff6b35; padding-bottom: 0.3em; }
    h2 { font-size: 1.5em; }
    h3 { font-size: 1.17em; }
    code {
      background: #f4f4f4;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      color: #ff6b35;
    }
    pre {
      background: #2d2d2d;
      color: #f8f8f2;
      padding: 15px;
      border-radius: 5px;
      overflow-x: auto;
    }
    pre code {
      background: transparent;
      color: #f8f8f2;
      padding: 0;
    }
    blockquote {
      border-left: 4px solid #ff6b35;
      padding-left: 20px;
      margin-left: 0;
      font-style: italic;
      color: #666;
    }
    a { color: #ff6b35; text-decoration: none; }
    a:hover { text-decoration: underline; }
    img { max-width: 100%; height: auto; }
    ul, ol { padding-left: 30px; }
    hr { border: none; border-top: 2px solid #ddd; margin: 2em 0; }
  </style>
</head>
<body>
  ${htmlContent}
  <footer style="margin-top: 3em; padding-top: 2em; border-top: 1px solid #ddd; color: #999; font-size: 0.9em;">
    <p>Erstellt mit <a href="https://zeneditor.com" style="color: #ff6b35;">ZenEditor</a></p>
  </footer>
</body>
</html>`;

    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeneditor-export-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setExporting(false);
      setExportType(null);
    }, 1000);
  };

  // Export as PDF (using browser print)
  const exportAsPDF = () => {
    setExporting(true);
    setExportType('pdf');

    const htmlContent = markdownToHTML(markdown);
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="de">
      <head>
        <meta charset="UTF-8">
        <title>ZenEditor PDF Export</title>
        <style>
          @media print {
            body { margin: 0; }
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            max-width: 210mm;
            margin: 20mm auto;
            padding: 20px;
            line-height: 1.6;
            color: #333;
          }
          h1, h2, h3 { color: #ff6b35; margin-top: 1.5em; page-break-after: avoid; }
          h1 { font-size: 2em; border-bottom: 2px solid #ff6b35; padding-bottom: 0.3em; }
          h2 { font-size: 1.5em; }
          h3 { font-size: 1.17em; }
          code {
            background: #f4f4f4;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
            color: #ff6b35;
          }
          pre {
            background: #2d2d2d;
            color: #f8f8f2;
            padding: 15px;
            border-radius: 5px;
            overflow-x: auto;
            page-break-inside: avoid;
          }
          pre code {
            background: transparent;
            color: #f8f8f2;
            padding: 0;
          }
          blockquote {
            border-left: 4px solid #ff6b35;
            padding-left: 20px;
            margin-left: 0;
            font-style: italic;
            color: #666;
            page-break-inside: avoid;
          }
          img { max-width: 100%; height: auto; page-break-inside: avoid; }
          ul, ol { padding-left: 30px; }
          hr { border: none; border-top: 2px solid #ddd; margin: 2em 0; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      setExporting(false);
      setExportType(null);
    }, 500);
  };

  // Export as Markdown
  const exportAsMarkdown = () => {
    setExporting(true);
    setExportType('md');

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeneditor-export-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setExporting(false);
      setExportType(null);
    }, 1000);
  };

  // Export as Plain Text
  const exportAsText = () => {
    setExporting(true);
    setExportType('txt');

    // Strip markdown formatting for plain text
    let plainText = markdown;
    plainText = plainText.replace(/#{1,6}\s/g, ''); // Remove headers
    plainText = plainText.replace(/\*\*([^*]+)\*\*/g, '$1'); // Remove bold
    plainText = plainText.replace(/\*([^*]+)\*/g, '$1'); // Remove italic
    plainText = plainText.replace(/\_([^_]+)\_/g, '$1'); // Remove italic
    plainText = plainText.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1'); // Links to text
    plainText = plainText.replace(/```[a-z]*\n/g, ''); // Remove code fence start
    plainText = plainText.replace(/```/g, ''); // Remove code fence end
    plainText = plainText.replace(/`([^`]+)`/g, '$1'); // Remove inline code
    plainText = plainText.replace(/^\>\s/gm, ''); // Remove blockquotes
    plainText = plainText.replace(/^\-\s/gm, '• '); // List bullets

    const blob = new Blob([plainText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeneditor-export-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setExporting(false);
      setExportType(null);
    }, 1000);
  };

  // Copy only to clipboard (without opening platform)
  const copyForPlatform = (platform) => {
    setExporting(true);
    setExportType(platform);

    let content = markdown;

    // Platform-specific formatting
    if (platform === 'medium') {
      content = markdown;
    } else if (platform === 'dev') {
      content = `---
title: Dein Titel hier
published: false
tags: javascript, webdev, tutorial
---

${markdown}`;
    } else if (platform === 'hashnode') {
      content = markdown;
    } else if (platform === 'linkedin') {
      // LinkedIn needs plain text
      content = markdown
        .replace(/#{1,6}\s/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    } else if (platform === 'wordpress') {
      content = markdown;
    } else if (platform === 'github') {
      content = markdown;
    }

    navigator.clipboard.writeText(content).then(() => {
      setExporting(false);
      setExportType(null);
      showToast(`Inhalt für ${platform.toUpperCase()} kopiert!`, 'success');
    }).catch((err) => {
      console.error('Clipboard error:', err);
      showToast('Fehler beim Kopieren. Bitte manuell kopieren.', 'error');
      setExporting(false);
      setExportType(null);
    });
  };

  // Open posting dialog for platforms
  const postToPlatform = (platform) => {
    setExporting(true);
    setExportType(platform);

    const urls = {
      medium: 'https://medium.com/new-story',
      wordpress: 'https://wordpress.com/post',
      dev: 'https://dev.to/new',
      hashnode: 'https://hashnode.com/create-story',
      linkedin: 'https://www.linkedin.com/post/new',
      github: 'https://gist.github.com/',
    };

    let content = markdown;

    // Platform-specific formatting
    if (platform === 'medium') {
      // Medium supports Markdown
      content = markdown;
    } else if (platform === 'dev') {
      // DEV.to supports Markdown with front matter
      content = `---
title: Dein Titel hier
published: false
tags: javascript, webdev, tutorial
---

${markdown}`;
    } else if (platform === 'hashnode') {
      // Hashnode supports Markdown
      content = markdown;
    } else if (platform === 'linkedin') {
      // LinkedIn needs plain text
      content = markdown
        .replace(/#{1,6}\s/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    }

    // WICHTIG: Fenster ZUERST öffnen (vor async clipboard operation)
    // Sonst wird es vom Pop-up-Blocker blockiert!
    const newWindow = window.open(urls[platform], '_blank');

    // Dann Content in Zwischenablage kopieren
    navigator.clipboard.writeText(content).then(() => {
      setExporting(false);
      setExportType(null);

      // Zeige nicht-blockierende Toast-Notification
      showToast(`Content für ${platform.toUpperCase()} kopiert! Tab geöffnet - drücke Cmd+V zum Einfügen.`, 'success');
    }).catch((err) => {
      console.error('Clipboard error:', err);
      showToast(`${platform.toUpperCase()}-Tab geöffnet. Bitte manuell kopieren (Cmd+A, Cmd+C).`, 'warning');
      setExporting(false);
      setExportType(null);
    });
  };

  return (
    <ModalWrapper onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Export & Veröffentlichen</h2>
          <p className="text-sm text-gray-600 mt-1">
            Exportiere deinen Content oder teile ihn direkt auf Plattformen
          </p>
        </div>

        {/* File Export Section */}
        <div className="mb-6">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-800">
            <FaFileAlt className="text-brand" />
            Datei-Export
          </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MonoButton
                onClick={exportAsHTML}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs"
              >
                <FaFileCode size={24} className="text-orange-400" />
                <span>HTML</span>
                {exportType === 'html' && <span className="text-xs text-green-400">✓ Exportiert</span>}
              </MonoButton>

              <MonoButton
                onClick={exportAsPDF}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs"
              >
                <FaFilePdf size={24} className="text-red-400" />
                <span>PDF</span>
                {exportType === 'pdf' && <span className="text-xs text-green-400">✓ Geöffnet</span>}
              </MonoButton>

              <MonoButton
                onClick={exportAsMarkdown}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs"
              >
                <FaFileAlt size={24} className="text-blue-400" />
                <span>Markdown</span>
                {exportType === 'md' && <span className="text-xs text-green-400">✓ Exportiert</span>}
              </MonoButton>

              <MonoButton
                onClick={exportAsText}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs"
              >
                <FaFileAlt size={24} className="text-gray-400" />
                <span>Text</span>
                {exportType === 'txt' && <span className="text-xs text-green-400">✓ Exportiert</span>}
              </MonoButton>
            </div>
          </div>

        {/* Platform Publishing Section */}
        <div className="border-t border-gray-300 pt-6">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-800">
            <FaMedium className="text-brand" />
            Direkt Veröffentlichen
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Klicke auf eine Plattform, um den Content zu kopieren und die Posting-Seite zu öffnen
          </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <MonoButton
                onClick={() => postToPlatform('medium')}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs hover:bg-black/30"
              >
                <FaMedium size={24} className="text-white" />
                <span>Medium</span>
                {exportType === 'medium' && <span className="text-xs text-green-400">✓ Kopiert</span>}
              </MonoButton>

              <MonoButton
                onClick={() => postToPlatform('wordpress')}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs hover:bg-black/30"
              >
                <FaWordpress size={24} className="text-blue-400" />
                <span>WordPress</span>
                {exportType === 'wordpress' && <span className="text-xs text-green-400">✓ Kopiert</span>}
              </MonoButton>

              <MonoButton
                onClick={() => postToPlatform('dev')}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs hover:bg-black/30"
              >
                <FaDev size={24} className="text-white" />
                <span>DEV.to</span>
                {exportType === 'dev' && <span className="text-xs text-green-400">✓ Kopiert</span>}
              </MonoButton>

              <MonoButton
                onClick={() => postToPlatform('hashnode')}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs hover:bg-black/30"
              >
                <FaHashtag size={24} className="text-blue-500" />
                <span>Hashnode</span>
                {exportType === 'hashnode' && <span className="text-xs text-green-400">✓ Kopiert</span>}
              </MonoButton>

              <MonoButton
                onClick={() => postToPlatform('linkedin')}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs hover:bg-black/30"
              >
                <FaLinkedin size={24} className="text-blue-600" />
                <span>LinkedIn</span>
                {exportType === 'linkedin' && <span className="text-xs text-green-400">✓ Kopiert</span>}
              </MonoButton>

              <MonoButton
                onClick={() => postToPlatform('github')}
                disabled={exporting}
                className="flex flex-col items-center gap-2 p-4 text-xs hover:bg-black/30"
              >
                <FaGithub size={24} className="text-white" />
                <span>GitHub Gist</span>
                {exportType === 'github' && <span className="text-xs text-green-400">✓ Kopiert</span>}
              </MonoButton>
            </div>
          </div>

        {/* Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded px-4 py-3">
          <p className="text-xs text-blue-800">
            💡 <strong>Tipp:</strong> Für beste Ergebnisse auf Plattformen wie Medium und DEV.to
            verwende Markdown-Formatierung mit Überschriften, Listen und Code-Blöcken.
          </p>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[60] animate-slide-up">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
            toast.type === 'success' ? 'bg-green-600' :
            toast.type === 'error' ? 'bg-red-600' :
            'bg-orange-600'
          }`}>
            <span className="text-white text-sm font-medium">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✗' : '⚠️'}
            </span>
            <span className="text-white text-sm">{toast.message}</span>
          </div>
        </div>
      )}
    </ModalWrapper>
  );
}

export default ZenEditorExportModal;
